// DealOpsRequestsCard — Ops Requests on one job, on the Overview tab
// (Sep 25, 2026).
//
// Two jobs: the "🛠 Request from Ops" button (sales roles), and the open
// requests already filed on this job so Robert sees them when he's in the
// deal. Closed ones collapse into a one-line count. Renders nothing for
// someone who can neither file nor see any request here.

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useRealtimeTable } from "../../lib/useRealtimeTable";
import OpsRequestCard from "../ops/OpsRequestCard";
import OpsRequestForm, { type OpsFormDeal } from "../ops/OpsRequestForm";
import {
  canFileRequests, fetchOpenDispatchQuestionDeals, fetchRequestsForDeal, fetchRoster, isOpen, sortForQueue,
  type ActorContext, type OpsRequest, type RosterLite,
} from "../../lib/opsRequests";

interface Props {
  deal: OpsFormDeal;
}

export default function DealOpsRequestsCard({ deal }: Props) {
  const { member, role, isSuperUser } = useAuth();
  const [rows, setRows] = useState<OpsRequest[]>([]);
  const [roster, setRoster] = useState<Record<string, RosterLite>>({});
  const [hasQ, setHasQ] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [showClosed, setShowClosed] = useState(false);
  const [formOpen, setFormOpen] = useState(false);

  const actor: ActorContext = useMemo(
    () => ({ memberId: member?.id ?? null, role, isSuperUser }),
    [member?.id, role, isSuperUser],
  );
  const canFile = canFileRequests(role, isSuperUser);

  const load = useCallback(async () => {
    try {
      const [reqs, people, q] = await Promise.all([
        fetchRequestsForDeal(deal.id),
        fetchRoster(),
        fetchOpenDispatchQuestionDeals([deal.id]),
      ]);
      setRows(reqs);
      setRoster(Object.fromEntries(people.map((m) => [m.id, m])));
      setHasQ(q.has(deal.id));
    } catch {
      /* additive card — a failure just shows the button */
    } finally {
      setLoaded(true);
    }
  }, [deal.id]);
  useEffect(() => { void load(); }, [load]);
  useRealtimeTable("ops_requests", () => { void load(); });

  const open = useMemo(() => sortForQueue(rows.filter(isOpen)), [rows]);
  const closed = useMemo(() => rows.filter((r) => !isOpen(r)), [rows]);

  if (!loaded) return null;
  if (!canFile && rows.length === 0) return null;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="font-bold text-bardin-ink text-sm">
          🛠 Ops Requests
          {open.length > 0 && <span className="ml-2 text-xs font-semibold text-slate-500">{open.length} open</span>}
        </h3>
        <div className="flex items-center gap-3">
          <Link to="/requests" className="text-xs text-bardin-teal font-semibold underline">Requests board →</Link>
          {canFile && member?.id && (
            <button type="button" onClick={() => setFormOpen(true)}
                    className="px-3 py-1.5 rounded-lg bg-bardin-orange text-white text-xs font-semibold shadow-sm">
              🛠 Request from Ops
            </button>
          )}
        </div>
      </div>

      {open.length === 0 && (
        <p className="text-xs text-slate-500">
          {canFile ? "Need Robert to do something on this job? File it here — homeowner and address come along automatically." : "No open requests on this job."}
        </p>
      )}

      {open.map((r) => (
        <OpsRequestCard key={r.id} r={r} actor={actor} roster={roster} onChanged={() => void load()} hasOpenQuestion={hasQ} onDealPage />
      ))}

      {closed.length > 0 && (
        <div>
          <button type="button" onClick={() => setShowClosed((v) => !v)} className="text-xs text-slate-500 underline">
            {showClosed ? "Hide" : "Show"} {closed.length} closed
          </button>
          {showClosed && (
            <div className="mt-2 space-y-2">
              {closed.map((r) => (
                <OpsRequestCard key={r.id} r={r} actor={actor} roster={roster} onChanged={() => void load()} onDealPage />
              ))}
            </div>
          )}
        </div>
      )}

      {member?.id && (
        <OpsRequestForm open={formOpen} onClose={() => setFormOpen(false)} deal={deal} actorId={member.id}
                        onFiled={() => { void load(); }} />
      )}
    </div>
  );
}
